import type {
  IMenuGroupAvailability,
  IMenuGroupDaysAvailable,
  IMenuGroupSeasonalAvailability,
} from "./MenuGroup";
import type { IModifierPriceOverride } from "./types";

/**
 * Represents a reference to a modifier group within an item or menu group.
 *
 * When stored on a MenuGroup's `modifierGroups[]` array, supports per-modifier
 * price overrides, visibility overrides, and scheduling overrides — following
 * the same "configuration by parent" pattern as SubgroupReference.
 *
 * Supports:
 * - Per-modifier price overrides (modifierPriceOverrides)
 * - Visibility override per parent context (isHidden, channel visibility)
 * - Service-type availability override (dine-in, takeout, etc.)
 * - Scheduling overrides (daysAvailable, seasonalAvailability)
 */
export interface IModifierGroupReference {
  readonly modifierGroupId: number;
  readonly position: number;
  readonly isRequired: boolean;
  readonly minSelection?: number;
  readonly maxSelection?: number;
  readonly allowMultipleSelection: boolean;
  readonly allowDuplicateSelection: boolean;
  /**
   * Per-modifier price overrides for this modifier group in this parent context.
   * When an item is ordered through a menu group that has this reference,
   * these prices take priority over the modifier's base price.
   * Resolved via ROOT→LEAF walk on the menu group path (first match wins).
   */
  readonly modifierPriceOverrides?: IModifierPriceOverride[];
  /**
   * `true` on a reference the BAR created on a menu group to carry per-menu
   * bar prices (`BarSpiritListService.saveMenuPrices`, design 2026-09-30 B4) —
   * a price-only reference. Clearing its last price removes it; a reference
   * without this mark was the manager's and is only ever edited, never removed.
   */
  readonly barPriceRef?: boolean;

  // ── Visibility overrides ──
  /** Hide this modifier group under this specific parent menu group */
  readonly isHidden?: boolean;
  /** Channel visibility overrides (undefined = use modifier group's own value) */
  readonly visibleInPOS?: boolean;
  readonly visibleInKiosk?: boolean;
  readonly visibleInOnlineOrdering?: boolean;
  readonly visibleToPartners?: boolean;
  readonly visibleInInvoices?: boolean;

  // ── Service-type availability override ──
  /** Override which service types this modifier group is available for under this parent */
  readonly availability?: IMenuGroupAvailability;

  // ── Scheduling overrides ──
  /** Override days/time availability under this parent */
  readonly daysAvailable?: IMenuGroupDaysAvailable;
  /** Override seasonal availability under this parent */
  readonly seasonalAvailability?: IMenuGroupSeasonalAvailability;
}

export type IModifierGroupReferenceData = Partial<IModifierGroupReference>;

export class ModifierGroupReference implements IModifierGroupReference {
  readonly modifierGroupId: number;
  readonly position: number;
  readonly isRequired: boolean;
  readonly minSelection?: number;
  readonly maxSelection?: number;
  readonly allowMultipleSelection: boolean;
  readonly allowDuplicateSelection: boolean;
  readonly modifierPriceOverrides?: IModifierPriceOverride[];
  readonly barPriceRef?: boolean;

  // Visibility overrides
  readonly isHidden?: boolean;
  readonly visibleInPOS?: boolean;
  readonly visibleInKiosk?: boolean;
  readonly visibleInOnlineOrdering?: boolean;
  readonly visibleToPartners?: boolean;
  readonly visibleInInvoices?: boolean;

  // Service-type availability override
  readonly availability?: IMenuGroupAvailability;

  // Scheduling overrides
  readonly daysAvailable?: IMenuGroupDaysAvailable;
  readonly seasonalAvailability?: IMenuGroupSeasonalAvailability;

  constructor(data: IModifierGroupReferenceData) {
    this.modifierGroupId = data.modifierGroupId!;
    this.position = data.position!;
    this.isRequired = data.isRequired ?? false;
    this.minSelection = data.minSelection;
    this.maxSelection = data.maxSelection;
    this.allowMultipleSelection = data.allowMultipleSelection ?? false;
    this.allowDuplicateSelection = data.allowDuplicateSelection ?? false;
    this.modifierPriceOverrides = data.modifierPriceOverrides;
    this.barPriceRef = data.barPriceRef;

    // Visibility
    this.isHidden = data.isHidden;
    this.visibleInPOS = data.visibleInPOS;
    this.visibleInKiosk = data.visibleInKiosk;
    this.visibleInOnlineOrdering = data.visibleInOnlineOrdering;
    this.visibleToPartners = data.visibleToPartners;
    this.visibleInInvoices = data.visibleInInvoices;

    // Service-type availability
    this.availability = data.availability;

    // Scheduling
    this.daysAvailable = data.daysAvailable;
    this.seasonalAvailability = data.seasonalAvailability;
  }

  toJSON(): IModifierGroupReference {
    const result: IModifierGroupReference = {
      modifierGroupId: this.modifierGroupId,
      position: this.position,
      isRequired: this.isRequired,
      minSelection: this.minSelection,
      maxSelection: this.maxSelection,
      allowMultipleSelection: this.allowMultipleSelection,
      allowDuplicateSelection: this.allowDuplicateSelection,
    };

    if (this.modifierPriceOverrides !== undefined) {
      (result as any).modifierPriceOverrides = this.modifierPriceOverrides;
    }
    // Kept through every load/save: the menu-group editor round-trips refs
    // through this class, and dropping the mark would turn a bar price-only
    // reference into a manager's that the bar may never remove again.
    if (this.barPriceRef !== undefined) {
      (result as any).barPriceRef = this.barPriceRef;
    }
    if (this.isHidden !== undefined) {
      (result as any).isHidden = this.isHidden;
    }
    if (this.visibleInPOS !== undefined) {
      (result as any).visibleInPOS = this.visibleInPOS;
    }
    if (this.visibleInKiosk !== undefined) {
      (result as any).visibleInKiosk = this.visibleInKiosk;
    }
    if (this.visibleInOnlineOrdering !== undefined) {
      (result as any).visibleInOnlineOrdering = this.visibleInOnlineOrdering;
    }
    if (this.visibleToPartners !== undefined) {
      (result as any).visibleToPartners = this.visibleToPartners;
    }
    if (this.visibleInInvoices !== undefined) {
      (result as any).visibleInInvoices = this.visibleInInvoices;
    }
    if (this.availability !== undefined) {
      (result as any).availability = this.availability;
    }
    if (this.daysAvailable !== undefined) {
      (result as any).daysAvailable = this.daysAvailable;
    }
    if (this.seasonalAvailability !== undefined) {
      (result as any).seasonalAvailability = this.seasonalAvailability;
    }

    return result;
  }

  static fromJSON(data: Record<string, unknown>): ModifierGroupReference {
    if (!data.modifierGroupId) throw new Error("ModifierGroupReference.fromJSON: modifierGroupId is missing.");

    return new ModifierGroupReference({
      ...data,
      modifierGroupId: data.modifierGroupId as number,
      position: (data.position as number) ?? 0,
      modifierPriceOverrides: data.modifierPriceOverrides as IModifierPriceOverride[] | undefined,
      barPriceRef: data.barPriceRef === true ? true : undefined,
      isHidden: data.isHidden as boolean | undefined,
      visibleInPOS: data.visibleInPOS as boolean | undefined,
      visibleInKiosk: data.visibleInKiosk as boolean | undefined,
      visibleInOnlineOrdering: data.visibleInOnlineOrdering as boolean | undefined,
      visibleToPartners: data.visibleToPartners as boolean | undefined,
      visibleInInvoices: data.visibleInInvoices as boolean | undefined,
      availability: data.availability as IMenuGroupAvailability | undefined,
      daysAvailable: data.daysAvailable as IMenuGroupDaysAvailable | undefined,
      seasonalAvailability: data.seasonalAvailability as IMenuGroupSeasonalAvailability | undefined,
    } as IModifierGroupReferenceData);
  }
} 